<script setup lang="ts">
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {FilterWidgetProps} from "@/utils/presenters/interfaces.ts";
import ReferenceWidget from "@/components/EditView/widgets/ReferenceWidget.vue";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {QSelect} from "quasar";
import GenericReferenceFieldWidgetUtil from "@/utils/editView/widgets/GenericReferenceFieldWidgetUtil.ts";
import {ref} from "vue";
import DropDownFieldWidgetUtil from "@/utils/editView/widgets/DropDownFieldWidgetUtil.ts";
import DropDownCloseIcon from "@/assets/widgetIcons/DropDownCloseIcon.vue";
import {type NamaNavigateToNextEmit, NamaVueWidget} from "@/utils/common/NamaVueWidget.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";

const props = defineProps<FilterWidgetProps>();
const fieldUtil = new GenericReferenceFieldWidgetUtil(props.presenter, props.fieldMetaData);
const options = ref(fieldUtil.allowedValues);
const popupVisible = ref<Boolean>(false);
const emit = defineEmits<NamaNavigateToNextEmit>()
function dropDrownFilter(val: any, update: any) {
  DropDownFieldWidgetUtil.dropDownFilterFn(options, fieldUtil, val, update);
}

function onEnterKeyDown(e: KeyboardEvent, doNotGoToNextWithEnter?: boolean) {
  if (popupVisible.value || doNotGoToNextWithEnter)
    return;
  emit("navigateToNext", {keyboardEvent: e});
}

const reference = ref<NamaVueWidget>()

function focus() {
  if (reference.value?.focus)
    reference.value.focus();
}

function canReceiveFocus() {
  if (reference.value?.canReceiveFocus)
    return reference.value?.canReceiveFocus();
  return false;
}

defineExpose({focus, canReceiveFocus})
</script>

<template>
  <WidgetContainer :field="field"
                   :entityType="presenter.entityType"
                   :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
                   :isFieldRequired="fieldUtil.isFieldRequired()" :isInsideComposite="isInsideComposite"
                   :doNotApplyThemeLayouts="doNotApplyThemeLayouts">
    <template v-slot:widget="{ showAsPassword, doNotGoToNextWithEnter }">
      <div class="desktop:tw-flex laptop:horizontal:quad:tw-gap-1 desktop:compact:tw-gap-1"
           :class="{'desktop:tw-flex-row tw-gap-4': !isGridColumnFilterField,'desktop:tw-flex-col tw-gap-2': isGridColumnFilterField,'tw-items-end':useNamaFlagsStore().showHelpMessagesActive}">
        <div class=" mobile:tw-pb-2 mobile:compact:tw-pb-1" :class="[isGridColumnFilterField?'tw-w-full':'tw-w-[40%] mobile:tw-w-full']"
             v-if="fieldUtil.shouldShowEntitySelector">
          <q-select @popup-show="popupVisible = true" @popup-hide="popupVisible = false" @keydown.enter="(e) => onEnterKeyDown(e, doNotGoToNextWithEnter)"
            :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
            :type="showAsPassword ? 'password' : 'text'"
            hide-bottom-space dense options-dense outlined use-input hide-selected fill-input
            :model-value="fieldUtil.formattedEntityTypeValue().value"
            @update:model-value="fieldUtil.onSelect"
            :options="options"
            option-label="title"
            option-value="id"
            @filter="dropDrownFilter"
            input-debounce="0"
            :error="!!props.presenter.fetchFieldErrorMsg(fieldUtil.entityTypeFieldId)"
            class="nama-customize-dense-input"
            :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
            input-class="tw-pl-[6px] tw-pr-[3px] tw-text-base tw-font-normal dark:tw-text-white tw-bg-transparent focus:tw-ring-0"
            options-selected-class="tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white"
                    popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark ag-custom-component-popup">
            <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldUtil.entityTypeFieldId)"
                          :error-msg="presenter.fetchFieldErrorMsg(fieldUtil.entityTypeFieldId)"/>
            <template v-slot:append v-if="!fieldUtil.isFieldDisabled(forceEnableField)">
              <q-avatar v-if="fieldUtil.formattedEntityTypeValue().value"
                        class="tw-cursor-pointer"
                        @click.stop.prevent="fieldUtil.onSelect(undefined)">
                <DropDownCloseIcon class="tw-w-[12px] tw-h-[12px]"/>
              </q-avatar>
            </template>
          </q-select>
        </div>
        <div class="mobile:tw-w-full mobile:tw-pb-2 mobile:compact:tw-pb-1"
             :class="{'tw-w-[60%]':(fieldUtil.shouldShowEntitySelector && !isGridColumnFilterField),
             'tw-w-[100%]':(!fieldUtil.shouldShowEntitySelector||isGridColumnFilterField)}">
          <ReferenceWidget :presenter="presenter" :isInsideComposite="true" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
                           :forceEnableField="forceEnableField" :field="field"
                           :fieldMetaData="fieldMetaData" ref="reference"
                           :default-entity-type="fieldUtil.defaultEntityType" @navigateToNext="(args)=>$emit('navigateToNext',args)"/>
        </div>
      </div>
    </template>
  </WidgetContainer>
</template>

<style>

</style>
